
export enum ScriptStatus {
  INITIAL = 'INITIAL',
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  ERROR = 'ERROR'
}

class ScriptLoader {
  private static instance: ScriptLoader;
  private scriptStatuses: Map<string, ScriptStatus> = new Map();
  private listeners: Map<string, Set<(status: ScriptStatus) => void>> = new Map();

  private constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.cleanup);
    }
  }

  static getInstance(): ScriptLoader {
    if (!ScriptLoader.instance) {
      ScriptLoader.instance = new ScriptLoader();
    }
    return ScriptLoader.instance;
  }

  getStatus(scriptId: string): ScriptStatus {
    return this.scriptStatuses.get(scriptId) || ScriptStatus.INITIAL;
  }

  private setStatus(scriptId: string, status: ScriptStatus): void {
    this.scriptStatuses.set(scriptId, status);
    this.notifyListeners(scriptId);
  }

  subscribe(scriptId: string, listener: (status: ScriptStatus) => void): void {
    if (!this.listeners.has(scriptId)) {
      this.listeners.set(scriptId, new Set());
    }
    this.listeners.get(scriptId)!.add(listener);
  }

  unsubscribe(scriptId: string, listener: (status: ScriptStatus) => void): void {
    this.listeners.get(scriptId)?.delete(listener);
  }

  private notifyListeners(scriptId: string): void {
    this.listeners.get(scriptId)?.forEach(listener => listener(this.getStatus(scriptId)));
  }

  loadScript(scriptSrc: string, scriptId: string): void {
    const isScriptAlreadyLoaded = this.scriptStatuses.has(scriptId) && this.getStatus(scriptId) === ScriptStatus.DONE;
    const isScriptInDocument = document.getElementById(scriptId) || document.querySelector(`script[src="${scriptSrc}"]`);

    if (isScriptAlreadyLoaded || isScriptInDocument) {
      this.notifyListeners(scriptId);
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = scriptSrc;
    script.type = 'text/javascript';

    script.addEventListener('load', this.onLoad);
    script.addEventListener('error', this.onError);

    document.head.appendChild(script);
    this.setStatus(scriptId, ScriptStatus.PROCESSING);
  }

  private cleanup = () => {
    this.scriptStatuses.forEach((_, scriptId) => {
      const script = document.getElementById(scriptId);
      if (script) {
        script.removeEventListener('load', this.onLoad);
        script.removeEventListener('error', this.onError);
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      }
    });
  };

  private onLoad = (event: Event) => {
    const script = event.target as HTMLScriptElement;
    this.setStatus(script.id, ScriptStatus.DONE);
  };

  private onError = (event: Event) => {
    const script = event.target as HTMLScriptElement;
    this.setStatus(script.id, ScriptStatus.ERROR);
  };
}

export default ScriptLoader.getInstance();
